import 'keen-slider/keen-slider.min.css'

import styled from '@emotion/styled'
import { Arrow } from 'app/components/Common/Arrow'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Spinner } from 'app/components/Common/Spinner'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'

export interface Props {
  clickedSlide?: number
  images: ImageProps[]
  isOpen?: boolean
}

export const Gallery = memo(function Gallery({
  clickedSlide,
  images,
  isOpen,
}: Props) {
  if (!images) {
    return null
  }

  const [currentSlide, setCurrentSlide] = React.useState(0)
  const [currentCategory, setCurrentCategory] = React.useState<any>()
  const [details, setDetails] = useState<any>(null)
  const [loaded, setLoaded] = React.useState<boolean[]>([])

  function positionStyle(idx: any) {
    if (!details) {
      return undefined
    }

    const position = details.slides[idx].distance
    const x = (galleryRef.current?.size || window.innerWidth) * -position
    return {
      transform: `translate3d(${x}px, 0px, 0px)`,
      WebkitTransform: `translate3d(${x}px, 0px, 0px)`,
    }
  }

  const [sliderRef, galleryRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1200,
    },
    drag: false,
    loop: images.length > 1 ? true : false,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
      // @ts-ignore
      setCurrentCategory(images[s.track.details.rel].category)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
    breakpoints: {
      '(max-width: 767px)': {
        drag: true,
      },
    },
  })

  useEffect(() => {
    const newLoaded = [...loaded]
    newLoaded[currentSlide] = true

    if (clickedSlide !== undefined) {
      galleryRef.current?.moveToIdx(clickedSlide, false, { duration: 0 })
    }

    setLoaded(newLoaded)

    if (isOpen) {
      document.addEventListener('keydown', function (e) {
        switch (e.keyCode) {
          case 37:
            galleryRef.current?.prev()
            break
          case 39:
            galleryRef.current?.next()
            break
        }
      })
    }
  }, [clickedSlide, currentSlide, galleryRef, sliderRef])

  return (
    <Container>
      <Category>{currentCategory}</Category>

      <Carousel ref={sliderRef}>
        {uniq(images).map((item, index) => (
          <Slide key={index} className="keen-slider__slide">
            <Inner style={positionStyle(index)}>
              {loaded[index] ? (
                <Image media="(min-width: 2800px)" {...item} />
              ) : (
                <Spinner variant="simple" />
              )}
            </Inner>
          </Slide>
        ))}
      </Carousel>

      {images && images.length > 1 ? (
        <Arrows row space="between">
          <Arrow
            onClick={(e) => e.stopPropagation() || galleryRef.current?.prev()}
          />
          <Arrow
            direction="R"
            onClick={(e) => e.stopPropagation() || galleryRef.current?.next()}
          />
        </Arrows>
      ) : null}

      <Counter>{`${currentSlide + 1}/${images.length}`}</Counter>
    </Container>
  )
})

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
`

const Carousel = styled.div`
  display: flex;
  width: calc(100% - 21.111vw);
  height: calc(100% - 16.375rem);
  outline: none;
  overflow: hidden;
  position: absolute;
  top: 8.75rem;
  left: 10.556vw;
  z-index: 2;

  @media (max-width: 1199px) {
    width: 100%;
    height: calc(100% - 8.75rem);
    top: 4.375rem;
    left: 0;
  }
`

const Slide = styled.div`
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const Category = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 2.875rem;
  position: absolute;
  top: 3.4375rem;
  left: 10.556vw;

  @media (max-width: 1199px) {
    font-size: 1.5rem;
    line-height: 1.875rem;
    top: 1.5rem;
    left: 1.875rem;
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const Arrows = styled(FlexBox)`
  position: absolute;
  top: 50%;
  right: calc(10.556vw - 3.625rem);
  left: calc(10.556vw - 3.625rem);
  transform: translateY(-50%);

  @media (max-width: 1199px) {
    top: auto;
    right: auto;
    bottom: 1.125rem;
    left: 1.875rem;
    transform: none;
  }

  @media (max-width: 767px) {
    left: 1.25rem;
  }
`

const Counter = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.875rem;
  opacity: 0.3;
  position: absolute;
  bottom: 3.4375rem;
  right: 10.556vw;

  @media (max-width: 1199px) {
    font-size: 1.125rem;
    line-height: 1.625rem;
    bottom: 1.125rem;
    right: 1.875rem;
  }

  @media (max-width: 767px) {
    right: 1.25rem;
  }
`
